import React, { useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import InsightsMatrixMemberDot from './InsightsMatrixMemberDot'
import InsightsMatrixCountBubble from './InsightsMatrixCountBubble'
import InsightsMatrixMemberPopover from './InsightsMatrixMemberPopover'
import InsightsClusterPopover from './InsightsClusterPopover'
import Popover from '../Popover'
import { fastTransition } from '../../styles/effects'
import { personaKeyForScores, IMemberPlot } from './InsightsMatrix'
import { ICompanyMember, ICompanyTerm } from '../../common/models/Company'
import { useAppState } from '../../context/AppContext'

export interface IScatterCluster {
  id: string
  memberIds: string[]
  x: number
  y: number
}

interface IProps {
  memberPlots: IMemberPlot[]
  memberById: Map<string, ICompanyMember>
  plotByMemberId: Map<string, IMemberPlot>
  plotByMemberIdPrev?: Map<string, IMemberPlot>
  compareActive?: boolean
  selectedTerm?: ICompanyTerm
  compareTo?: ICompanyTerm
  pinnedMemberId?: string
  hoveredMemberId?: string
  selectedClusterId?: string
  onMemberHover?: (memberId: string | undefined) => void
  onMemberPin?: (memberId: string | undefined) => void
  onClusterClick?: (cluster: IScatterCluster) => void
  onPopoverClose?: () => void
  onSelectMember?: (member: ICompanyMember) => void
}

const CLUSTER_DIAMETER_PX = 28

const InsightsMatrixScatterView: React.FC<IProps> = ({
  memberPlots,
  memberById,
  plotByMemberId,
  plotByMemberIdPrev,
  compareActive,
  selectedTerm,
  compareTo,
  pinnedMemberId,
  hoveredMemberId,
  selectedClusterId,
  onMemberHover,
  onMemberPin,
  onClusterClick,
  onPopoverClose,
  onSelectMember,
}) => {
  const { t } = useTranslation()
  const { theme } = useAppState()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const dotRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const bubbleRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const [plotSize, setPlotSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  })
  const [listHoveredMemberId, setListHoveredMemberId] = useState<string>()

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = (): void => {
      const rect = el.getBoundingClientRect()
      setPlotSize({ width: rect.width, height: rect.height })
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (!selectedClusterId) setListHoveredMemberId(undefined)
  }, [selectedClusterId])

  const clusters = useMemo<IScatterCluster[]>(() => {
    const scoreOf = (p: IMemberPlot): number => p.x + (100 - p.y)
    const sorted = [...memberPlots].sort((a, b) => scoreOf(b) - scoreOf(a))
    const result: IScatterCluster[] = []
    const canCluster = plotSize.width > 0 && plotSize.height > 0
    const rX = canCluster ? (CLUSTER_DIAMETER_PX / plotSize.width) * 100 : 0
    const rY = canCluster ? (CLUSTER_DIAMETER_PX / plotSize.height) * 100 : 0
    for (const plot of sorted) {
      const existing = canCluster
        ? result.find((c) => {
            const dx = (c.x - plot.x) / rX
            const dy = (c.y - plot.y) / rY
            return dx * dx + dy * dy <= 1
          })
        : undefined
      if (existing) {
        const total = existing.memberIds.length + 1
        existing.x = (existing.x * existing.memberIds.length + plot.x) / total
        existing.y = (existing.y * existing.memberIds.length + plot.y) / total
        existing.memberIds.push(plot.memberId)
      } else {
        result.push({
          id: `cluster_${result.length}`,
          memberIds: [plot.memberId],
          x: plot.x,
          y: plot.y,
        })
      }
    }
    return result
  }, [memberPlots, plotSize])

  const pinnedMember = pinnedMemberId ? memberById.get(pinnedMemberId) : null
  const pinnedPlot = pinnedMemberId ? plotByMemberId.get(pinnedMemberId) : null
  const pinnedPlotPrev =
    pinnedMemberId && plotByMemberIdPrev
      ? plotByMemberIdPrev.get(pinnedMemberId) ?? null
      : null
  const pinnedAnchor = pinnedMemberId
    ? dotRefs.current[pinnedMemberId] ?? null
    : null

  const selectedCluster = selectedClusterId
    ? clusters.find((c) => c.id === selectedClusterId)
    : null

  const focusedMemberId =
    pinnedMemberId ?? listHoveredMemberId ?? hoveredMemberId
  const focusedPlot = focusedMemberId
    ? plotByMemberId.get(focusedMemberId)
    : null
  const focusedPlotPrev =
    focusedMemberId && plotByMemberIdPrev
      ? plotByMemberIdPrev.get(focusedMemberId) ?? null
      : null
  const selectedClusterMembers = useMemo<ICompanyMember[]>(() => {
    if (!selectedCluster) return []
    return selectedCluster.memberIds
      .map((id) => memberById.get(id))
      .filter((m): m is ICompanyMember => !!m)
  }, [selectedCluster, memberById])
  const clusterAnchor = selectedClusterId
    ? bubbleRefs.current[selectedClusterId] ?? null
    : null

  const isDimmedState = !!focusedMemberId || !!selectedClusterId
  const isInSelectedCluster = (memberId: string): boolean =>
    !!selectedCluster && selectedCluster.memberIds.includes(memberId)
  const dotIsDimmed = (memberId: string): boolean => {
    if (!isDimmedState) return false
    if (focusedMemberId) return memberId !== focusedMemberId
    return !isInSelectedCluster(memberId)
  }

  const renderMemberDot = (memberId: string, key: string): React.ReactNode => {
    const member = memberById.get(memberId)
    const plot = plotByMemberId.get(memberId)
    if (!member || !plot) return null
    const skillText = plot.skillAssessed
      ? `${Math.round(100 - plot.y)}/100`
      : t('general.states.noFinalScore')
    const cultureText = plot.cultureAssessed
      ? `${Math.round(plot.x)}/100`
      : t('general.states.noFinalScore')
    // The persona blends both axes, so only mention it when both were assessed.
    const persona =
      plot.skillAssessed && plot.cultureAssessed
        ? `, ${t(personaKeyForScores(100 - plot.y, plot.x))}`
        : ''
    const ariaLabel = `${member.name}, ${t('general.skills')} ${skillText}, ${t(
      'general.feedback.culture'
    )} ${cultureText}${persona}`
    const isFocus = focusedMemberId === memberId
    return (
      <DotWrapper
        key={key}
        style={{ left: `${plot.x}%`, top: `${plot.y}%` }}
        $dimmed={dotIsDimmed(memberId)}
        $focused={isFocus}
      >
        <InsightsMatrixMemberDot
          ref={(el) => {
            dotRefs.current[memberId] = el
          }}
          member={member}
          ariaLabel={ariaLabel}
          hovered={
            hoveredMemberId === memberId || listHoveredMemberId === memberId
          }
          pinned={pinnedMemberId === memberId}
          onMouseEnter={() => onMemberHover?.(memberId)}
          onMouseLeave={() => onMemberHover?.(undefined)}
          onFocus={() => onMemberHover?.(memberId)}
          onBlur={() => onMemberHover?.(undefined)}
          onClick={() =>
            onMemberPin?.(pinnedMemberId === memberId ? undefined : memberId)
          }
        />
      </DotWrapper>
    )
  }

  return (
    <ScatterContainer ref={containerRef}>
      {focusedPlot && focusedPlotPrev && (
        <>
          <MovementSvg
            aria-hidden
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
          >
            <defs>
              <marker
                id="movement-arrowhead"
                viewBox="0 0 10 10"
                refX="8"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path
                  d="M 0 0 L 10 5 L 0 10 z"
                  fill={theme.colors.primaryFadeSemi}
                />
              </marker>
            </defs>
            <line
              x1={focusedPlotPrev.x}
              y1={focusedPlotPrev.y}
              x2={focusedPlot.x}
              y2={focusedPlot.y}
              stroke={theme.colors.primaryFadeSemi}
              strokeWidth={0.5}
              vectorEffect="non-scaling-stroke"
              markerEnd="url(#movement-arrowhead)"
              opacity={0.7}
            />
          </MovementSvg>
          <MovementPrevDot
            aria-hidden
            style={{
              left: `${focusedPlotPrev.x}%`,
              top: `${focusedPlotPrev.y}%`,
            }}
          />
        </>
      )}
      {focusedPlot && (
        <CrosshairLayer aria-live="polite">
          <HCrosshairLine
            $dashed={!focusedPlot.skillAssessed}
            style={{
              top: `${focusedPlot.y}%`,
              width: `${focusedPlot.x}%`,
            }}
          />
          <VCrosshairLine
            $dashed={!focusedPlot.cultureAssessed}
            style={{
              left: `${focusedPlot.x}%`,
              top: `${focusedPlot.y}%`,
              height: `${100 - focusedPlot.y}%`,
            }}
          />
          <YAxisPill style={{ top: `${focusedPlot.y}%` }}>
            {focusedPlot.skillAssessed ? Math.round(100 - focusedPlot.y) : '-'}
          </YAxisPill>
          <XAxisPill style={{ left: `${focusedPlot.x}%` }}>
            {focusedPlot.cultureAssessed ? Math.round(focusedPlot.x) : '-'}
          </XAxisPill>
        </CrosshairLayer>
      )}
      {clusters.map((cluster) => {
        const positionStyle = {
          left: `${cluster.x}%`,
          top: `${cluster.y}%`,
        }
        const isExpanded =
          cluster.id === selectedClusterId && cluster.memberIds.length > 1

        if (cluster.memberIds.length === 1) {
          return renderMemberDot(cluster.memberIds[0], `dot-${cluster.id}`)
        }

        if (isExpanded) {
          return (
            <React.Fragment key={`expanded-${cluster.id}`}>
              <BubbleWrapper style={positionStyle} $hidden>
                <InsightsMatrixCountBubble
                  ref={(el) => {
                    bubbleRefs.current[cluster.id] = el
                  }}
                  count={cluster.memberIds.length}
                  total={memberPlots.length}
                  ariaLabel=""
                />
              </BubbleWrapper>
              {cluster.memberIds.map((memberId) =>
                renderMemberDot(memberId, `expanded-${cluster.id}-${memberId}`)
              )}
            </React.Fragment>
          )
        }

        const clusterAriaLabel = `${cluster.memberIds.length} ${t(
          'general.people'
        )}`

        return (
          <BubbleWrapper
            key={`bubble-${cluster.id}`}
            style={positionStyle}
            $dimmed={isDimmedState}
          >
            <InsightsMatrixCountBubble
              ref={(el) => {
                bubbleRefs.current[cluster.id] = el
              }}
              count={cluster.memberIds.length}
              total={memberPlots.length}
              selected={selectedClusterId === cluster.id}
              ariaLabel={clusterAriaLabel}
              onClick={() => onClusterClick?.(cluster)}
            />
          </BubbleWrapper>
        )
      })}
      {pinnedMemberId &&
        (() => {
          const cluster = clusters.find((c) =>
            c.memberIds.includes(pinnedMemberId)
          )
          if (!cluster) return null
          if (cluster.memberIds.length === 1) return null
          if (cluster.id === selectedClusterId) return null
          return renderMemberDot(pinnedMemberId, `pinned-${pinnedMemberId}`)
        })()}
      <Popover
        anchor={pinnedAnchor}
        isOpen={!!pinnedMember && !!pinnedPlot}
        placement={(pinnedPlot?.x ?? 50) < 50 ? 'left' : 'right'}
        onClose={() => onPopoverClose?.()}
      >
        {pinnedMember && pinnedPlot && (
          <InsightsMatrixMemberPopover
            member={pinnedMember}
            cultureScore={pinnedPlot.x}
            skillScore={100 - pinnedPlot.y}
            cultureAssessed={pinnedPlot.cultureAssessed}
            skillAssessed={pinnedPlot.skillAssessed}
            previousCultureScore={
              pinnedPlotPrev?.cultureAssessed ? pinnedPlotPrev.x : undefined
            }
            previousSkillScore={
              pinnedPlotPrev?.skillAssessed ? 100 - pinnedPlotPrev.y : undefined
            }
            compareActive={compareActive}
            selectedTerm={selectedTerm}
            compareTo={compareTo}
            onClose={() => onPopoverClose?.()}
            onShowDetails={
              onSelectMember
                ? () => {
                    onSelectMember(pinnedMember)
                    onPopoverClose?.()
                  }
                : undefined
            }
          />
        )}
      </Popover>
      <InsightsClusterPopover
        anchor={clusterAnchor}
        isOpen={!!selectedCluster && selectedClusterMembers.length > 0}
        placement={(selectedCluster?.x ?? 50) < 50 ? 'left' : 'right'}
        members={selectedClusterMembers}
        plotByMemberId={plotByMemberId}
        plotByMemberIdPrev={plotByMemberIdPrev}
        compareActive={compareActive}
        selectedTerm={selectedTerm}
        compareTo={compareTo}
        onClose={() => onPopoverClose?.()}
        onShowDetails={onSelectMember}
        onMemberHover={setListHoveredMemberId}
      />
    </ScatterContainer>
  )
}

export default InsightsMatrixScatterView

const ScatterContainer = styled.div`
  position: absolute;
  inset: 0;
`

const MovementSvg = styled.svg`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 4;
  overflow: visible;
`

const MovementPrevDot = styled.div`
  position: absolute;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primaryFadeSemi};
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 4;
`

const CrosshairLayer = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
`

const DotWrapper = styled.div<{ $dimmed?: boolean; $focused?: boolean }>`
  position: absolute;
  transform: translate(-50%, -50%);
  // Focused dot sits above the crosshair/movement layer (z-index 4) so the
  // avatar caps its own crosshair; un-focused dots stay below it.
  z-index: ${({ $focused }) => ($focused ? 5 : 2)};
  opacity: ${({ $dimmed }) => ($dimmed ? 0.25 : 1)};
  transition: opacity ${fastTransition};
`

const BubbleWrapper = styled.div<{ $dimmed?: boolean; $hidden?: boolean }>`
  position: absolute;
  transform: translate(-50%, -50%);
  z-index: 2;
  opacity: ${({ $dimmed }) => ($dimmed ? 0.25 : 1)};
  visibility: ${({ $hidden }) => ($hidden ? 'hidden' : 'visible')};
  transition: opacity ${fastTransition};
`

// A gradient-based dash gives control over the gap size (4px dash, 8px gap),
// which a plain `border: dashed` can't; solid axes keep the flat fill.
const HCrosshairLine = styled.div<{ $dashed?: boolean }>`
  position: absolute;
  left: 0;
  height: 1px;
  ${({ theme, $dashed }) =>
    $dashed
      ? `background: repeating-linear-gradient(to right, ${theme.colors.primary} 0 4px, transparent 4px 12px);`
      : `background-color: ${theme.colors.primary};`}
  opacity: 0.35;
  pointer-events: none;
  z-index: 4;
`

const VCrosshairLine = styled.div<{ $dashed?: boolean }>`
  position: absolute;
  width: 1px;
  ${({ theme, $dashed }) =>
    $dashed
      ? `background: repeating-linear-gradient(to bottom, ${theme.colors.primary} 0 4px, transparent 4px 12px);`
      : `background-color: ${theme.colors.primary};`}
  opacity: 0.35;
  pointer-events: none;
  z-index: 4;
`

const AxisPill = styled.div`
  position: absolute;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.lightest};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  z-index: 6;
`

const YAxisPill = styled(AxisPill)`
  left: 0;
  transform: translate(-50%, -50%);
`

const XAxisPill = styled(AxisPill)`
  bottom: 0;
  transform: translate(-50%, 50%);
`
